import { ApiError, apiClient } from '../../lib/api-client';
import type {
  GeneralSettingsResponse,
  SettingsListParams,
  SettingsListResponse,
  SettingsLookupResource,
  SettingsModuleSlug,
  SettingsRecord,
  SettingsRecordResponse,
  SettingsStatus,
  TdsListResponse,
  TdsApplyChange,
  TdsManagementRecord,
} from './types';

const settingsPath = (module: SettingsModuleSlug) => `/settings/${module}`;

export const fetchSettingsList = async (module: SettingsModuleSlug, params: SettingsListParams) => {
  const response = await apiClient.get<SettingsListResponse>(settingsPath(module), { params });
  return {
    ...response.data,
    data: response.data.data.filter((record) => record.status !== 'archived'),
  };
};

export const fetchGeneralSettings = async (): Promise<SettingsRecord | null> => {
  try {
    const response = await apiClient.get<GeneralSettingsResponse>(settingsPath('general-settings'));
    return response.data.data;
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) return null;
    throw error;
  }
};

export const saveGeneralSettings = async (payload: Record<string, unknown>) => {
  const response = await apiClient.patch<SettingsRecordResponse>(settingsPath('general-settings'), payload);
  return response.data.data;
};

export const createSetting = async (module: SettingsModuleSlug, payload: Record<string, unknown>) => {
  const response = await apiClient.post<SettingsRecordResponse>(settingsPath(module), payload);
  return response.data.data;
};

export const updateSetting = async (
  module: SettingsModuleSlug,
  recordId: string,
  payload: Record<string, unknown>,
) => {
  const response = await apiClient.patch<SettingsRecordResponse>(`${settingsPath(module)}/${recordId}`, payload);
  return response.data.data;
};

export const updateSettingStatus = async (
  module: SettingsModuleSlug,
  recordId: string,
  status: SettingsStatus,
) => {
  const response = await apiClient.patch<SettingsRecordResponse>(`${settingsPath(module)}/${recordId}/status`, { status });
  return response.data.data;
};

export const fetchSettingsLookup = async (resource: SettingsLookupResource, params?: { parentId?: string; limit?: number; search?: string; selectedIds?: string }) => {
  const response = await apiClient.get<{ success: true; data: SettingsRecord[] }>(`/settings/lookups/${resource}`, {
    params: { limit: 500, ...params },
  });
  return response.data.data;
};

export const fetchTdsManagement = async (params: {
  page: number;
  limit: number;
  section?: string;
  billNumber?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}) => {
  const response = await apiClient.get<TdsListResponse>(settingsPath('tds-management'), { params });
  return response.data;
};

export const applyTdsPayments = async (changes: TdsApplyChange[]) => {
  const response = await apiClient.patch<{ success: true; data: TdsManagementRecord[] }>(
    `${settingsPath('tds-management')}/apply`,
    { changes },
  );
  return response.data.data;
};
